import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
//import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
//import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { toNgbDate } from '@helpers/datetime-helper';
import { NgbCalendar, NgbDate, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DateFormat, DateParserFormatter } from '@services/date-parser';
import { DateTime } from 'luxon';

export type DateRange = { from: DateTime | null; to: DateTime | null };

@UntilDestroy()
@Component({
    selector: 'app-date-range',
    templateUrl: './date-range.component.html',
    styleUrls: ['./date-range.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateRangeComponent),
            multi: true
        },
        DateParserFormatter
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DateRangeComponent implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor {
    @ViewChild('dateRange') public dateRange: NgbInputDatepicker;
    //public faCalendarDay = faCalendarDay;
    public faXmark = faXmark;
    public hoveredDate: NgbDate | null = null;
    public minDate: NgbDate | null;
    public maxDate: NgbDate | null;
    public fromDate: NgbDate | null;
    public toDate: NgbDate | null;
    public datePlaceholder: string;
    private readonly calendar = inject(NgbCalendar);
    public readonly formatter = inject(DateParserFormatter);
    private readonly cdr = inject(ChangeDetectorRef);

    /**
     * Disables future date selections – as long as the max datetime input is not set.
     */
    @Input() public disableFutureDates = true;

    @Input()
    public set min(dateTime: DateTime | null) {
        this.minDate = dateTime ? toNgbDate(dateTime) : null;
        this.cdr.markForCheck();
    }

    @Input()
    public set max(dateTime: DateTime | null) {
        this.maxDate = dateTime ? toNgbDate(dateTime) : null;
        this.cdr.markForCheck();
    }

    @Input()
    public set from(dateTime: DateTime | null) {
        this.fromDate = dateTime ? toNgbDate(dateTime) : null;
        this.updateDatePickerStartDate();
        this.cdr.markForCheck();
    }

    @Input()
    public set to(dateTime: DateTime | null) {
        this.toDate = dateTime ? toNgbDate(dateTime) : null;
        this.updateDatePickerStartDate();
        this.cdr.markForCheck();
    }

    @Output() public dateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();

    private dateRangeValue: DateRange;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched = (): void => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onChange: (dateRange: DateRange) => void = () => {};

    public formControl!: FormControl;

    public ngOnInit() {
        this.datePlaceholder = this.formatter.dateFormats[DateFormat.LongDate].placeholder;

        if (this.disableFutureDates && !this.maxDate) {
            this.maxDate = toNgbDate(DateTime.local());
        }
    }

    public ngAfterViewInit(): void {
        this.updateDatePickerStartDate();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const typedChanges = changes as PropertyChanges<this>;

        if (typedChanges.disableFutureDates?.currentValue && typedChanges.max?.currentValue) {
            throw new Error("Cannot use 'disableFutureDates' in conjunction the with 'max' datetime input");
        }
    }

    public writeValue(dateRange: DateRange): void {
        this.dateRangeValue = dateRange;
    }

    public registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    public setDisabledState?(_isDisabled: boolean): void {}

    public onDateSelection(date: NgbDate) {
        if (!this.fromDate && !this.toDate) {
            this.fromDate = date;
        } else if (this.fromDate && !this.toDate) {
            // Ensure toDate is not before fromDate
            if (date.before(this.fromDate)) {
                this.toDate = this.fromDate;
                this.fromDate = date;
            } else {
                this.toDate = date;
            }
            this.dateRange.close();
        } else {
            this.toDate = null;
            this.fromDate = date;
        }

        if (this.fromDate && this.toDate) {
            let to = this.getDateTime(this.toDate);
            to = to.endOf('day');

            this.dateRangeValue = { from: this.getDateTime(this.fromDate), to };

            this.onChange(this.dateRangeValue);
            this.dateRangeChange.emit(this.dateRangeValue);
        }
    }

    public isHovered(date: NgbDate) {
        return (
            this.fromDate &&
            !this.toDate &&
            this.hoveredDate &&
            date.after(this.fromDate) &&
            date.before(this.hoveredDate)
        );
    }

    public isInside(date: NgbDate) {
        return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
    }

    public isRange(date: NgbDate) {
        return (
            date.equals(this.fromDate) ||
            (this.toDate && date.equals(this.toDate)) ||
            this.isInside(date) ||
            this.isHovered(date)
        );
    }

    public toggle() {
        this.dateRange.toggle();
    }

    public clear(event: MouseEvent) {
        event.stopPropagation();
        this.fromDate = null;
        this.from = null;
        this.toDate = null;
        this.to = null;

        this.dateRangeValue = null;
        this.onChange(this.dateRangeValue);
        this.dateRangeChange.emit(null);
    }

    public onClosed() {
        this.onTouched();
    }

    private getDateTime(date: NgbDate | null): DateTime | null {
        return date && DateTime.local(date.year, date.month, date.day);
    }

    private updateDatePickerStartDate(): void {
        if (this.dateRange) {
            if (this.fromDate) {
                this.dateRange.startDate = this.fromDate;
            } else if (this.toDate) {
                this.dateRange.startDate = this.toDate;
            } else {
                const beginningOfLastMonthDate = DateTime.local()
                    .minus({
                        month: 1
                    })
                    .startOf('month');
                this.dateRange.startDate = toNgbDate(beginningOfLastMonthDate);
            }
        }
    }
}
